import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Alert, Button, Flex, Input } from 'antd'
import { useTranslation } from 'react-i18next'
import { currentLanguage } from '@/app/i18n'
import { grantAccess, requestOtp, type GuestAccessResult, type OtpIssued } from '@/features/portal/api'
import { translatePortalError } from '@/shared/api/errors'
import { StepHeader } from '@/shared/ui/StepHeader'

interface SmsCodeStepProps {
  zoneId: string
  mac: string
  ip: string | null
  nasIdentifier: string
  userAgent: string
  challenge: OtpIssued
  phone: string
  campaignId: string | null
  onAccess: (result: GuestAccessResult) => void
  onChangeNumber: () => void
}

/**
 * Soniyalik hisoblagich. `resetKey` o'zgarganda (masalan qayta
 * yuborilgan challenge'ning yangi `challengeId`si) hisoblagich QAYTA
 * BOSHLANADI — `seconds` qiymati ilgarigisi bilan tasodifan bir xil
 * bo'lsa ham (React `seconds` o'zgarmasa effektni qayta ishga
 * tushirmaydi, shuning uchun alohida kalit kerak).
 */
function useCountdown(resetKey: string, seconds: number): number {
  const [remaining, setRemaining] = useState(seconds)

  useEffect(() => {
    setRemaining(seconds)
    // `seconds` ataylab dependency'da yo'q: `resetKey` o'zgarganda bir
    // martalik "boshlang'ich qiymat" sifatida o'qiladi, xolos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])

  useEffect(() => {
    if (remaining <= 0) return
    const id = window.setTimeout(() => setRemaining((s) => s - 1), 1000)
    return () => window.clearTimeout(id)
  }, [remaining])

  return remaining
}

export function SmsCodeStep({
  zoneId,
  mac,
  ip,
  nasIdentifier,
  userAgent,
  challenge,
  phone,
  campaignId,
  onAccess,
  onChangeNumber,
}: SmsCodeStepProps) {
  const { t } = useTranslation()
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Qayta yuborilgan kodning o'z muddat/challengeId'si bo'ladi — shuning
  // uchun challenge alohida state'da, propsdagi boshlang'ich qiymatdan
  // boshlab qayta yuborilganda YANGILANADI.
  const [activeChallenge, setActiveChallenge] = useState(challenge)
  const remaining = useCountdown(activeChallenge.challengeId, activeChallenge.resendAfterSeconds)

  const verifyMutation = useMutation({
    mutationFn: () =>
      grantAccess({
        zoneId,
        mac,
        ip,
        nasIdentifier,
        userAgent,
        credential: { method: 'SMS_OTP', challengeId: activeChallenge.challengeId, code, phoneE164: phone },
        campaignId,
        locale: currentLanguage(),
      }),
  })

  const resendMutation = useMutation({
    mutationFn: () => requestOtp({ zoneId, phone, mac, requestIp: ip, locale: currentLanguage() }),
  })

  const canVerify = code.length === 6

  const handleVerify = () => {
    setError(null)
    if (!canVerify) {
      setError(t('sms.codeInvalid'))
      return
    }
    verifyMutation.mutate(undefined, {
      onSuccess: onAccess,
      onError: (err) => setError(translatePortalError(err, t).message),
    })
  }

  const handleResend = () => {
    setError(null)
    resendMutation.mutate(undefined, {
      onSuccess: (issued) => {
        setActiveChallenge(issued)
        setCode('')
      },
      onError: (err) => setError(translatePortalError(err, t).message),
    })
  }

  return (
    <Flex vertical gap={16}>
      <StepHeader
        title={t('sms.codeTitle')}
        subtitle={t('sms.codeSubtitle', { phone: activeChallenge.maskedDestination })}
      />

      {error ? <Alert type="error" showIcon message={error} /> : null}

      <Flex justify="center">
        <Input.OTP length={6} size="large" value={code} onChange={setCode} autoFocus />
      </Flex>

      <Flex vertical gap={8}>
        <Button
          type="primary"
          size="large"
          block
          loading={verifyMutation.isPending}
          disabled={!canVerify}
          onClick={handleVerify}
        >
          {verifyMutation.isPending ? t('sms.verifying') : t('sms.verify')}
        </Button>
        <Button
          type="text"
          block
          disabled={remaining > 0}
          loading={resendMutation.isPending}
          onClick={handleResend}
        >
          {remaining > 0 ? t('sms.resendIn', { seconds: remaining }) : t('sms.resend')}
        </Button>
        <Button type="text" block onClick={onChangeNumber} disabled={verifyMutation.isPending}>
          {t('sms.changeNumber')}
        </Button>
      </Flex>
    </Flex>
  )
}
